/**
 * ConceptoRepository - Repositorio unificado para Conceptos (taxonomía)
 *
 * Implementación compartida entre web (Dexie) y mobile (SQLite).
 * Gestiona el catálogo de conceptos (globales + por empresa) y las tablas de
 * relación `conceptos_ocultos` / `conceptos_confirmados` (PK compuesta
 * empresa_id+concepto_id), que se sincronizan como filas normales vía los
 * eventos de TABLE_MAPPING.
 */

import { v4 as uuidv4 } from 'uuid';
import { IConceptoRepository, ConceptoOculto } from '../interfaces/IConceptoRepository';
import { BaseRepository } from './BaseRepository';
import { IDatabaseAdapter } from '../adapters/IDatabaseAdapter';
import { IProfileRepository } from '../interfaces/IProfileRepository';
import { Concepto } from '../../../types/administracion';
import { normalizarTexto, kgDeCantidad } from '../../../helpers/conceptosHelper';
import { emitInsert, emitDelete } from '../syncHelpers';

/**
 * Repo de las tablas de relación `conceptos_ocultos` / `conceptos_confirmados`
 * (PK compuesta empresa_id+concepto_id, sin columna id).
 *
 * No se registra en DatabaseFactory: el PULL full-snapshot (syncPull) lo consume
 * vía el campo `repo` inline del PullTableConfig, y las mutaciones de la app
 * pasan por ConceptoRepository (ocultar/desocultar/confirmar).
 */
export class ConceptoRelacionRepository extends BaseRepository<ConceptoOculto, string> {

  constructor(db: IDatabaseAdapter, tabla: 'conceptos_ocultos' | 'conceptos_confirmados') {
    super(db, tabla, tabla);
  }

  /** Clave efectiva de cada fila: la pareja empresa/concepto (no hay campo id). */
  protected get pkField(): string {
    return 'concepto_id';
  }

  async add(data: Omit<ConceptoOculto, 'id'>): Promise<string> {
    await this.db.insert(this.tableName, data);
    this.emitSync('INSERT', data as any);
    return data.concepto_id;
  }

  /** Las tablas de relación son insert/delete solamente (no hay UPDATE en Supabase). */
  async update(): Promise<void> {
    throw new Error(`${this.tableName}: las tablas de relación no se actualizan`);
  }

  /** Borra por PK compuesta [empresa_id, concepto_id] (o concepto_id suelto). */
  async delete(key: string | [string, string]): Promise<void> {
    await this.db.delete(this.tableName, key);
    // El DELETE viaja por concepto_id: TABLE_CONFIG define esa PK para el
    // .in()/.eq() del sync y el RLS de Supabase acota a la empresa del usuario.
    this.emitSync('DELETE', Array.isArray(key) ? key[1] : key);
  }
}

export class ConceptoRepository extends BaseRepository<Concepto, string> implements IConceptoRepository {

  /**
   * Sub-repos de las tablas de relación, para el PULL full-snapshot (syncPull,
   * configs de SyncManager.buildPullConfigs). Las mutaciones de la app no los
   * usan: pasan por ocultar()/desocultar()/confirmar().
   */
  readonly ocultosRepo: ConceptoRelacionRepository;
  readonly confirmadosRepo: ConceptoRelacionRepository;

  constructor(db: IDatabaseAdapter, profileRepo?: IProfileRepository) {
    super(db, 'conceptos', 'conceptos'); // tabla local, tabla Supabase

    this.supportsSoftDelete = false;
    this.autoTimestamp = true;

    this.ocultosRepo = new ConceptoRelacionRepository(db, 'conceptos_ocultos');
    this.confirmadosRepo = new ConceptoRelacionRepository(db, 'conceptos_confirmados');

    if (profileRepo) {
      this.setProfileRepository(profileRepo);
    }
  }

  // ==================== CRUD Base ====================

  async add(data: Omit<Concepto, 'id'>): Promise<string> {
    const id = uuidv4();
    const now = this.getCurrentTimestamp();
    const profile = await this.getCurrentProfile();

    // Los conceptos creados desde el cliente son siempre de la empresa activa;
    // los globales (empresa_id null) solo se crean por seed del backend.
    const record: Concepto = {
      ...data,
      id,
      created_at: data.created_at ?? now,
      updated_at: now,
      empresa_id: data.empresa_id ?? profile?.empresa_activa_id ?? null,
    };

    await this.db.insert('conceptos', record);
    this.emitSync('INSERT', record);

    return id;
  }

  async update(data: Partial<Concepto> & { id: string }): Promise<void> {
    const updatedData = {
      ...data,
      updated_at: this.getCurrentTimestamp(),
    };
    await this.db.update('conceptos', data.id, updatedData);
    this.emitSync('UPDATE', updatedData as any);
  }

  async delete(id: string): Promise<void> {
    await this.db.delete('conceptos', id);
    this.emitSync('DELETE', id);
  }

  // ==================== Taxonomía (spec 2026-08-07) ====================

  async listarVisibles(empresaId: string): Promise<Concepto[]> {
    const [todos, ocultos] = await Promise.all([
      this.db.getAll<Concepto>('conceptos'),
      this.db.getAll<{ empresa_id: string; concepto_id: string }>('conceptos_ocultos'),
    ]);
    const ocultosSet = new Set(
      ocultos.filter(o => o.empresa_id === empresaId).map(o => o.concepto_id)
    );

    // Candidatos: activos, no ocultos para esta empresa, globales o propios
    const candidatos = todos.filter(c =>
      c.activo &&
      !ocultosSet.has(c.id) &&
      (c.empresa_id === null || c.empresa_id === empresaId)
    );

    // Regla de sombreado (spec §3.8): el propio de igual nombre+categoría
    // (normalizados) tapa al global
    const propios = candidatos.filter(c => c.empresa_id === empresaId);
    const clavesPropias = new Set(
      propios.map(c => `${c.categoria_id}::${normalizarTexto(c.nombre)}`)
    );
    return candidatos.filter(c =>
      c.empresa_id !== null ||
      !clavesPropias.has(`${c.categoria_id}::${normalizarTexto(c.nombre)}`)
    );
  }

  /**
   * TODOS los conceptos de la empresa + globales — sin filtro de activo, sin
   * excluir ocultos, sin sombreado — para el ÍNDICE DE CLASIFICACIÓN de gastos
   * históricos (KAN-171, spec §12.4): la clasificación de un gasto ya cargado
   * NO depende de la visibilidad actual del catálogo. Ocultar o desactivar un
   * concepto reescribe qué ofrece el picker (listarVisibles), no cómo se
   * clasifican sus gastos. Paridad con el bot (cargarConceptos sin filtro
   * activo). El picker de creación sigue usando listarVisibles.
   */
  async listarTodosParaClasificacion(empresaId: string): Promise<Concepto[]> {
    const todos = await this.db.getAll<Concepto>('conceptos');
    return todos.filter(c => c.empresa_id === null || c.empresa_id === empresaId);
  }

  /**
   * Globales ocultos por la empresa (los que tienen fila en conceptos_ocultos):
   * el complemento de listarVisibles para el modo "Ver ocultos" de la card
   * de gestión, donde se pueden volver a mostrar.
   */
  async listarOcultos(empresaId: string): Promise<Concepto[]> {
    const [todos, ocultos] = await Promise.all([
      this.db.getAll<Concepto>('conceptos'),
      this.db.getAll<{ empresa_id: string; concepto_id: string }>('conceptos_ocultos'),
    ]);
    const ocultosSet = new Set(
      ocultos.filter(o => o.empresa_id === empresaId).map(o => o.concepto_id)
    );
    return todos.filter(c => c.empresa_id === null && ocultosSet.has(c.id));
  }

  async getFrecuencias(empresaId: string): Promise<Record<string, number>> {
    const fuentes = await Promise.all([
      this.db.getAll<{ concepto_id?: string; empresa_id: string; is_deleted?: boolean }>('gastos'),
      this.db.getAll<{ concepto_id?: string; empresa_id: string; is_deleted?: boolean }>('ingresos'),
      this.db.getAll<{ concepto_id?: string; empresa_id: string; is_deleted?: boolean }>('stocks_historico'),
    ]);
    const freq: Record<string, number> = {};
    for (const lista of fuentes) {
      for (const t of lista) {
        // Las tres fuentes son soft-delete: los eliminados no cuentan como uso
        if (t.concepto_id && !t.is_deleted && t.empresa_id === empresaId) {
          freq[t.concepto_id] = (freq[t.concepto_id] || 0) + 1;
        }
      }
    }
    return freq;
  }

  /**
   * Textos históricos por concepto (P3a Task 4): la columna `concepto` de
   * gastos/ingresos y `nombre` de stocks_historico, deduplicados por forma
   * normalizada (gana la primera aparición). Los movimientos sin detalle
   * guardan el nombre del concepto: el picker los filtra al buscar.
   */
  async getDetallesHistoricos(empresaId: string): Promise<Record<string, string[]>> {
    type Movimiento = { concepto_id?: string; texto?: string; empresa_id: string; is_deleted?: boolean };
    const [gastos, ingresos, stocks] = await Promise.all([
      this.db.getAll<{ concepto_id?: string; concepto?: string; empresa_id: string; is_deleted?: boolean }>('gastos'),
      this.db.getAll<{ concepto_id?: string; concepto?: string; empresa_id: string; is_deleted?: boolean }>('ingresos'),
      this.db.getAll<{ concepto_id?: string; nombre?: string; empresa_id: string; is_deleted?: boolean }>('stocks_historico'),
    ]);
    const movimientos: Movimiento[] = [
      ...gastos.map(g => ({ concepto_id: g.concepto_id, texto: g.concepto, empresa_id: g.empresa_id, is_deleted: g.is_deleted })),
      ...ingresos.map(i => ({ concepto_id: i.concepto_id, texto: i.concepto, empresa_id: i.empresa_id, is_deleted: i.is_deleted })),
      ...stocks.map(s => ({ concepto_id: s.concepto_id, texto: s.nombre, empresa_id: s.empresa_id, is_deleted: s.is_deleted })),
    ];
    const detalles: Record<string, string[]> = {};
    const vistos = new Set<string>();
    for (const m of movimientos) {
      const texto = (m.texto ?? '').trim();
      const clave = `${m.concepto_id}::${normalizarTexto(texto)}`;
      if (!m.concepto_id || !texto || m.is_deleted || m.empresa_id !== empresaId) continue;
      if (vistos.has(clave)) continue;
      vistos.add(clave);
      (detalles[m.concepto_id] ??= []).push(texto);
    }
    return detalles;
  }

  async crearMiVersion(conceptoGlobalId: string, cambios: Partial<Concepto> = {}): Promise<Concepto> {
    const global = await this.get(conceptoGlobalId);
    if (!global || global.empresa_id !== null) {
      throw new Error('El concepto no es global');
    }

    const profile = await this.getCurrentProfile();
    // Copiar sin id/timestamps: add() genera id nuevo y timestamps frescos
    const camposCopiados: Omit<Concepto, 'id'> = { ...global };
    delete (camposCopiados as Partial<Concepto>).id;
    delete camposCopiados.created_at;
    delete camposCopiados.updated_at;

    const propio: Omit<Concepto, 'id'> = {
      ...camposCopiados,
      ...cambios,
      empresa_id: profile?.empresa_activa_id ?? cambios.empresa_id ?? '',
      origen: 'copia_global',
    };

    const id = await this.add(propio);
    return { ...propio, id };
  }

  // ==================== Ocultos / Confirmados (tablas de relación) ====================
  //
  // Son tablas de Supabase mapeadas en TABLE_MAPPING: sus mutaciones se emiten
  // con los helpers compartidos de syncHelpers (mismo canal que usa emitSync)
  // para que el SyncManager las persista/pushee como filas normales. El DELETE
  // viaja por concepto_id: TABLE_CONFIG define esa PK para el `.in()` y el RLS
  // de Supabase acota el borrado a la empresa del usuario.

  async ocultar(conceptoId: string, empresaId: string): Promise<void> {
    await this.insertRelacion('conceptos_ocultos', conceptoId, empresaId);
  }

  async desocultar(conceptoId: string, empresaId: string): Promise<void> {
    await this.deleteRelacion('conceptos_ocultos', conceptoId, empresaId);
  }

  async confirmar(conceptoId: string, empresaId: string): Promise<void> {
    await this.insertRelacion('conceptos_confirmados', conceptoId, empresaId);
  }

  async estaConfirmado(conceptoId: string, empresaId: string): Promise<boolean> {
    const confirmados = await this.db.getAll<{ empresa_id: string; concepto_id: string }>('conceptos_confirmados');
    return confirmados.some(c => c.empresa_id === empresaId && c.concepto_id === conceptoId);
  }

  /**
   * Inserta la fila (empresa_id, concepto_id) en la tabla de relación,
   * idempotente (si ya existe, no hace nada) para no chocar con la PK compuesta.
   */
  private async insertRelacion(tabla: 'conceptos_ocultos' | 'conceptos_confirmados', conceptoId: string, empresaId: string): Promise<void> {
    const existentes = await this.db.getAll<{ empresa_id: string; concepto_id: string }>(tabla);
    if (existentes.some(r => r.empresa_id === empresaId && r.concepto_id === conceptoId)) {
      return; // ya estaba
    }
    const row = {
      empresa_id: empresaId,
      concepto_id: conceptoId,
      created_at: this.getCurrentTimestamp(),
    };
    await this.db.insert(tabla, row);
    emitInsert(tabla, row);
  }

  /**
   * Borra la fila (empresa_id, concepto_id) de la tabla de relación.
   * La PK compuesta de Dexie se pasa como array [empresa_id, concepto_id].
   */
  private async deleteRelacion(tabla: 'conceptos_ocultos' | 'conceptos_confirmados', conceptoId: string, empresaId: string): Promise<void> {
    await this.db.delete(tabla, [empresaId, conceptoId]);
    emitDelete(tabla, conceptoId);
  }

  // ==================== Precio de compra (spec §5.2) ====================

  async getUltimoPrecioCompra(conceptoId: string, empresaId?: string): Promise<number | null> {
    const [historico, concepto] = await Promise.all([
      this.db.getAll<{
        concepto_id?: string;
        empresa_id?: string;
        tipo_operacion: string;
        fecha_evento: string;
        cantidad_operacion: number;
        precio_total: number;
        is_deleted?: boolean;
      }>('stocks_historico'),
      this.get(conceptoId),
    ]);
    if (!concepto) return null;

    // Scope por empresa (default: la activa del profile): para conceptos
    // globales usados por varias empresas, el último precio no puede venir de
    // OTRA empresa. Sin empresa resoluble se deja sin filtro (fallback
    // defensivo, mismo criterio que getAllForActiveEmpresa).
    const empresaEfectiva = empresaId ?? (await this.getCurrentProfile())?.empresa_activa_id ?? null;

    const ultimaCompra = historico
      .filter(h =>
        h.concepto_id === conceptoId &&
        h.tipo_operacion === 'COMPRA' &&
        !h.is_deleted &&
        (!empresaEfectiva || h.empresa_id === empresaEfectiva)
      )
      .sort((a, b) => (b.fecha_evento ?? '').localeCompare(a.fecha_evento ?? ''))[0];

    if (!ultimaCompra) return null;

    const kg = kgDeCantidad(ultimaCompra.cantidad_operacion, concepto);
    if (!kg || kg <= 0) return null;

    return ultimaCompra.precio_total / kg;
  }
}
